import CONFIG from '../config/config';
import { instanceWithV2 as APIClient } from './SimpleAPIClient';
import { Customer } from './CustomersAPI';
import { serializeParamsForSpringBoot } from './serializeParamsForSpringBoot';

export type SaleInstalment = {
  id?: number;
  amount: number;
  currency: string;
  dueDate?: string; // YYYY-MM-DD
  paymentMethod?: 'TRANSFER' | 'CARD' | 'CASH';
  isPaid?: boolean;
  paidDate?: string; // YYYY-MM-DD
  invoiceReference?: string;
  notes?: string;
  amountInWords?: string;
};

export type Sale = {
  id?: number;
  saleDate: string; // YYYY-MM-DD format
  clientName: string;
  customerName?: string;
  customerSurname?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerId?: number;
  customer?: Customer; // For UI convenience, not sent to backend
  weddingDate?: string; // ISO string
  deliveryDate?: string; // ISO string
  completionDate?: string; // ISO string
  clientPersonalID?: string;
  clientAddress?: string;
  firstFollowUpMeetingDate?: string; // ISO string
  employeeId?: string;
  dressName: string;
  dressDescription: string;
  productId?: string;
  productType?: 'CATALOG_ITEM' | 'CATALOG_ITEM_VARIANT';
  productVariant?: string;
  dressVeil?: string;
  dressVeilLength?: number;
  bust?: string;
  waist?: string;
  hips?: string;
  size?: string;
  measurements?: string;
  totalPrice: number;
  totalPriceInWords?: string;
  currency: string;
  paymentStatus: string;
  status: string;
  receiptNumber: string;
  hasContractPDF?: boolean;
  attachmentUrl?: string;
  orderId?: number;
  externalOrderId?: number;
  b2bType?: string;
  opinionRequestScheduledDate?: string;
  opinionRequestScheduleId?: string;
  instalments?: SaleInstalment[];
};

type PaginatedResponse = {
  content: Sale[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};

type BodyApiResponse = any;

const SalesAPI = {
  get: ({
    status,
    statuses,
    paymentStatuses,
    search,
    date,
    page = 0,
    size = 50,
  }: {
    status?: string;
    statuses?: string[];
    paymentStatuses?: string[];
    search?: string;
    date?: string;
    page?: number;
    size?: number;
  }) => {
    const params: Record<string, unknown> = { page, size };
    if (search) params.search = search;
    if (date) params.date = date;
    if (statuses && statuses.length > 0) {
      params.statuses = statuses;
    } else if (status) {
      params.status = status;
    }
    if (paymentStatuses && paymentStatuses.length > 0) {
      params.paymentStatuses = paymentStatuses;
    }
    return APIClient(CONFIG.API_ENDPOINT).get<PaginatedResponse>(`v1/sales`, {
      params,
      paramsSerializer: serializeParamsForSpringBoot,
    });
  },
  getById: (id: number | string) => APIClient(CONFIG.API_ENDPOINT).get<Sale>(`/v1/sales/${id}`),
  add: (sale: Sale) => APIClient(CONFIG.API_ENDPOINT).post<Sale>(`/v1/sales`, sale),
  update: (id: number | string, sale: Sale) => APIClient(CONFIG.API_ENDPOINT).put<Sale>(`/v1/sales/${id}`, sale),
  remove: (id: number | string) => APIClient(CONFIG.API_ENDPOINT).delete<BodyApiResponse>(`/v1/sales/${id}`),
  addToProduction: (id: number | string, formData: { deliveryDate: string; prioritized: boolean; pendingMeasures: boolean }) => 
    APIClient(CONFIG.API_ENDPOINT).post<any>(`/v1/sales/${id}/add-to-production`, formData),
  scheduleOpinionRequest: (id: number | string, scheduledDate?: string) => {
    const queryParams = new URLSearchParams();
    if (scheduledDate) queryParams.append("scheduledDate", scheduledDate);
    const queryString = queryParams.toString();
    return APIClient(CONFIG.API_ENDPOINT).post<Sale>(`/v1/sales/${id}/opinion-request${queryString ? `?${queryString}` : ''}`);
  },
  deleteOpinionRequest: (id: number | string) => APIClient(CONFIG.API_ENDPOINT).delete<Sale>(`/v1/sales/${id}/opinion-request`),
  generateContractPDF: (id: number | string, request?: { completionDate?: string; collectionDate?: string; firstFollowUpMeetingDate?: string }) => APIClient(CONFIG.API_ENDPOINT).post<Blob>(
    `/v1/sales/${id}/generate-contract`,
    request || {},
    {
      responseType: "blob",
      headers: {
        'accept': 'application/pdf'
      }
    }
  ),
  sendToProducent: (id: number | string) => APIClient(CONFIG.API_ENDPOINT).post<Sale>(`/v1/sales/${id}/send-to-producent`),
  syncInstalmentsToProductionOrder: (id: number | string) =>
    APIClient(CONFIG.API_ENDPOINT).post<unknown>(`/v1/sales/${id}/sync-instalments-to-production-order`),
};

export function isInternalSale(sale: Sale): boolean {
  return !sale.b2bType || sale.b2bType === '';
}

export function isExternalSale(sale: Sale): boolean {
  return !!sale.b2bType && sale.b2bType !== '';
}

export default SalesAPI;
